import React, { useState, useEffect } from 'react';

/**
 * RedFlagsSection Component
 * Critical screening section for lumbar spine - detects cauda equina syndrome
 * This could literally save a patient's life
 */
const RedFlagsSection = ({ config, formData, onChange }) => {
  const [redFlagScore, setRedFlagScore] = useState(0);
  const [alertLevel, setAlertLevel] = useState(null);
  const [criticalFlags, setCriticalFlags] = useState([]);

  // Calculate red flag score whenever answers change
  useEffect(() => {
    if (!config.fields) return;

    let score = 0;
    const triggered = [];

    config.fields.forEach(field => {
      const value = formData[field.id];
      if (value === 'Yes' && field.weight) {
        score += field.weight;
        if (field.urgent) {
          triggered.push(field.label);
        }
      }
    });

    setRedFlagScore(score);
    setCriticalFlags(triggered);

    // Determine alert level
    if (score >= (config.alerts?.critical?.threshold || 10)) {
      setAlertLevel('critical');
    } else if (score >= (config.alerts?.urgent?.threshold || 7)) {
      setAlertLevel('urgent');
    } else if (score >= (config.alerts?.concerning?.threshold || 5)) {
      setAlertLevel('concerning');
    } else {
      setAlertLevel(null);
    }
  }, [formData, config]);

  const handleFieldChange = (fieldId, value) => {
    onChange(fieldId, value);
  };

  const renderField = (field) => {
    const value = formData[field.id] || '';
    const isUrgent = field.urgent;

    return (
      <div 
        key={field.id}
        className={`field-group ${isUrgent ? 'urgent-field' : ''}`}
      >
        <label className="field-label">
          {isUrgent && <span className="urgent-indicator">🚨 </span>}
          {field.label}
          {field.required && <span className="required">*</span>}
        </label>

        {field.type === 'radio' && (
          <div className="radio-group">
            {field.options.map(option => (
              <label key={option} className="radio-option">
                <input
                  type="radio"
                  name={field.id}
                  value={option}
                  checked={value === option}
                  onChange={(e) => handleFieldChange(field.id, e.target.value)}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        )}

        {field.type === 'textarea' && (
          <textarea
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            rows={field.rows || 3}
            className="form-textarea"
          />
        )}

        {field.type === 'text' && (
          <input
            type="text"
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            placeholder={field.placeholder}
            className="form-input"
          />
        )}

        {field.note && (
          <p className="field-note">{field.note}</p>
        )}
      </div>
    );
  };

  const getAlertConfig = () => {
    if (!alertLevel || !config.alerts) return null;
    return config.alerts[alertLevel];
  };

  const alertConfig = getAlertConfig();

  return (
    <div className="red-flags-section">
      <style jsx>{`
        .red-flags-section {
          border: 3px solid #dc2626;
          border-radius: 8px;
          padding: 24px;
          background: #fef2f2;
          margin: 24px 0;
        }

        .emergency-header {
          background: #fee2e2;
          border-left: 4px solid #dc2626;
          padding: 16px;
          border-radius: 6px;
          margin-bottom: 24px;
        }

        .emergency-header h3 {
          color: #991b1b;
          font-size: 20px;
          font-weight: 700;
          margin: 0 0 8px 0;
        }

        .emergency-header p {
          color: #7f1d1d;
          margin: 0;
          font-size: 14px;
        }

        .intro-text {
          background: white;
          padding: 12px 16px;
          border-radius: 6px;
          margin-bottom: 20px;
          border-left: 4px solid #f59e0b;
          font-size: 14px;
          color: #78350f;
        }

        .field-group {
          background: white;
          padding: 16px;
          border-radius: 6px;
          margin-bottom: 16px;
          border: 1px solid #e5e7eb;
        }

        .urgent-field {
          border-left: 4px solid #dc2626;
          background: #fef2f2;
          border-color: #fecaca;
        }

        .field-label {
          display: block;
          font-weight: 600;
          color: #1f2937;
          margin-bottom: 12px;
          font-size: 15px;
        }

        .urgent-indicator {
          color: #dc2626;
          font-size: 18px;
        }

        .required {
          color: #dc2626;
          margin-left: 4px;
        }

        .radio-group {
          display: flex;
          gap: 24px;
        }

        .radio-option {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          padding: 8px 12px;
          border-radius: 4px;
          transition: background 0.2s;
        }

        .radio-option:hover {
          background: #f9fafb;
        }

        .radio-option input[type="radio"] {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }

        .radio-option span {
          font-size: 15px;
          color: #374151;
        }

        .form-textarea,
        .form-input {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 14px;
          font-family: inherit;
          transition: border-color 0.2s;
        }

        .form-textarea:focus,
        .form-input:focus {
          outline: none;
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
        }

        .form-textarea {
          resize: vertical;
        }

        .field-note {
          margin-top: 8px;
          font-size: 13px;
          color: #7c3aed;
          font-style: italic;
        }

        .alert-box {
          margin-top: 24px;
          padding: 20px;
          border-radius: 8px;
          border: 3px solid;
          animation: pulse 2s ease-in-out infinite;
        }

        .alert-box.critical {
          background: #fee2e2;
          border-color: #dc2626;
          color: #991b1b;
        }

        .alert-box.urgent {
          background: #fed7aa;
          border-color: #ea580c;
          color: #7c2d12;
        }

        .alert-box.concerning {
          background: #fef3c7;
          border-color: #f59e0b;
          color: #78350f;
        }

        .alert-header {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 20px;
          font-weight: 700;
          margin-bottom: 12px;
        }

        .alert-icon {
          font-size: 32px;
        }

        .alert-message {
          font-size: 16px;
          line-height: 1.6;
          margin: 0;
        }

        .triggered-flags {
          margin-top: 16px;
          padding-top: 16px;
          border-top: 2px solid currentColor;
        }

        .triggered-flags-title {
          font-weight: 600;
          margin-bottom: 8px;
        }

        .triggered-flags ul {
          margin: 8px 0 0 0;
          padding-left: 24px;
        }

        .triggered-flags li {
          margin: 4px 0;
        }

        .score-indicator {
          display: inline-block;
          background: white;
          padding: 4px 12px;
          border-radius: 20px;
          font-weight: 600;
          margin-left: 8px;
          font-size: 14px;
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.85;
          }
        }

        @media (max-width: 640px) {
          .red-flags-section {
            padding: 16px;
          }

          .radio-group {
            flex-direction: column;
            gap: 12px;
          }
        }
      `}</style>

      <div className="emergency-header">
        <h3>🚨 CRITICAL: Red Flag Screening</h3>
        <p>
          {config.intro || 'The following questions screen for serious conditions requiring urgent assessment'}
        </p>
      </div>

      {config.intro && (
        <div className="intro-text">
          {config.intro}
        </div>
      )}

      <div className="fields-container">
        {config.fields && config.fields.map(field => renderField(field))}
      </div>
    </div>
  );
};
   
export default RedFlagsSection;
